import React, { useState, useEffect } from 'react';
import { Star, Send, Check, AlertCircle, BadgeCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewForm = ({ userRole = 'patient', appointmentId = null, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const isStaff = ['doctor', 'ortottista', 'admin'].includes(userRole);

  useEffect(() => {
    loadExistingReview();
  }, []);

  const loadExistingReview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reviews/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setExistingReview(response.data);
        setRating(response.data.rating);
        setTitle(response.data.title || '');
        setContent(response.data.content || '');
      }
    } catch (error) {
      console.error('Error loading existing review:', error);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Seleziona un punteggio (1-5 stelle)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reviews`, {
        rating,
        title: title || null,
        content: content || null,
        review_type: isStaff ? 'staff' : 'patient',
        appointment_id: appointmentId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Grazie per la tua recensione!');
      setExistingReview({ rating, title, content, pending_approval: true });
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Errore durante l\'invio della recensione');
    } finally {
      setLoading(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <h3 className="text-lg font-bold mb-4" style={{color: '#2A9FCB'}}>
        {isStaff ? '💼 La tua esperienza lavorativa' : '⭐ Lascia una recensione'}
      </h3>

      {/* Badge Utente Verificato */}
      <div className="mb-4 flex items-center gap-2 text-sm text-sky-700 bg-sky-50 px-3 py-2 rounded-lg">
        <BadgeCheck className="w-5 h-5 text-sky-600" />
        <span>La tua recensione mostrerà il badge <strong>"Utente Verificato"</strong></span>
      </div>

      {existingReview && (
        <div className="mb-4 p-3 rounded-lg flex items-start gap-2" style={{
          background: existingReview.is_published ? '#DCFCE7' : existingReview.pending_approval ? '#FEF3C7' : '#FEE2E2',
          border: `1px solid ${existingReview.is_published ? '#86EFAC' : existingReview.pending_approval ? '#FCD34D' : '#FECACA'}`
        }}>
          {existingReview.is_published ? (
            <Check className="w-5 h-5 text-green-600 mt-0.5" />
          ) : existingReview.pending_approval ? (
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          )}
          <div>
            <p className="text-sm font-medium" style={{
              color: existingReview.is_published ? '#166534' : existingReview.pending_approval ? '#92400E' : '#991B1B'
            }}>
              {existingReview.is_published 
                ? 'La tua recensione è stata pubblicata!' 
                : existingReview.pending_approval
                  ? 'Recensione in attesa di approvazione'
                  : 'Recensione non approvata'}
            </p>
            <p className="text-xs" style={{
              color: existingReview.is_published ? '#22C55E' : existingReview.pending_approval ? '#D97706' : '#EF4444'
            }}>
              {existingReview.is_published 
                ? 'Grazie per il tuo feedback!' 
                : existingReview.pending_approval
                  ? 'L\'amministratore la esaminerà a breve'
                  : 'Puoi modificarla e riprovare'}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Star Rating - 1 to 5 stars */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            {isStaff ? 'Come valuti la tua esperienza di lavoro?' : 'Come valuti la tua esperienza?'}
          </label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    star <= (hoverRating || rating)
                      ? 'fill-current text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-sm mt-2 font-medium" style={{
              color: rating === 5 ? '#10B981' : rating === 4 ? '#22C55E' : rating === 3 ? '#3B82F6' : '#F59E0B'
            }}>
              {rating === 5 && '⭐⭐⭐⭐⭐ Eccezionale!'}
              {rating === 4 && '⭐⭐⭐⭐ Eccellente!'}
              {rating === 3 && '⭐⭐⭐ Molto buono'}
              {rating === 2 && '⭐⭐ Buono'}
              {rating === 1 && '⭐ Da migliorare'}
            </p>
          )}
        </div>

        {/* Title - Optional */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Titolo <span className="text-gray-400">(opzionale)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isStaff ? "Es: Un ambiente stimolante" : "Es: Professionalità eccezionale"}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
            maxLength={100}
            data-testid="review-title-input"
          />
        </div>

        {/* Content - Optional */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            {isStaff ? 'Racconta la tua esperienza' : 'La tua recensione'} <span className="text-gray-400">(opzionale)</span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={isStaff 
              ? "Descrivi il tuo ambiente di lavoro, il team, le opportunità di crescita..."
              : "Descrivi la tua esperienza con noi..."
            }
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2A9FCB] resize-none"
            rows={4}
            maxLength={1000}
            data-testid="review-content-input"
          />
          {content && <p className="text-xs text-gray-400 text-right mt-1">{content.length}/1000</p>}
        </div>

        {/* Info Box - Rimosso messaggio sulla moderazione per non sembrare manipolazione */}
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-start gap-2">
          <BadgeCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-green-800">
              Ogni recensione viene verificata per garantire autenticità.
            </p>
            <p className="text-xs text-green-600 mt-1">
              "Utente Verificato" ✓ verrà mostrato sotto la tua recensione
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || rating === 0}
          className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
          style={{background: '#2A9FCB'}}
          data-testid="submit-review-btn"
        >
          {loading ? (
            'Invio in corso...'
          ) : (
            <>
              <Send className="w-4 h-4" />
              Aggiungi Recensione
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ReviewForm;
